import { useId } from "react";
import type { InputHTMLAttributes, TextareaHTMLAttributes } from "react";

export const fieldClass =
  "w-full rounded-md border border-line/70 bg-surface-raised/60 px-3 py-2 text-sm text-ink outline-none transition-colors placeholder:text-ink-muted/70 focus:border-brand disabled:opacity-60";

interface BaseProps {
  label?: string;
  hint?: string;
  error?: string;
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement>, BaseProps {}

export default function Input({ label, hint, error, className = "", id, ...rest }: InputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className="block text-xs font-medium text-ink-muted">
          {label}
        </label>
      )}
      <input
        id={inputId}
        {...rest}
        className={`${fieldClass} ${error ? "border-danger/60" : ""} ${className}`}
      />
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : (
        hint && <p className="text-xs text-ink-muted">{hint}</p>
      )}
    </div>
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, BaseProps {}

export function Textarea({ label, hint, error, className = "", id, ...rest }: TextareaProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className="block text-xs font-medium text-ink-muted">
          {label}
        </label>
      )}
      <textarea
        id={inputId}
        {...rest}
        className={`${fieldClass} min-h-24 resize-y ${error ? "border-danger/60" : ""} ${className}`}
      />
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : (
        hint && <p className="text-xs text-ink-muted">{hint}</p>
      )}
    </div>
  );
}
